import React, { useState } from 'react';
import { ActivityIndicator, Alert, Pressable, StyleSheet, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';

import { GlassButton } from '../../../components/ui/GlassButton';
import { signInHeadline } from '../../../config/branding';
import { colors, spacing, typography } from '../../../core/theme/tokens';
import type { AuthStackParamList } from '../../../core/navigation/types';
import { goBackFromAuth } from '../../../core/navigation/navigationRef';
import { getDeviceId } from '../../../services/deviceIdService';
import { authService } from '../services/authService';
import { AuthBrandHeader } from '../components/AuthBrandHeader';
import { AppleSignInButton } from '../components/AppleSignInButton';
import { AuthPhoneField, GoogleSignInButton } from '../components/AuthFormControls';
import { AuthModalFrame } from '../components/AuthModalFrame';
import { useAppleLogin } from '../hooks/useAppleLogin';
import { useGoogleLogin } from '../hooks/useGoogleLogin';

type Props = NativeStackScreenProps<AuthStackParamList, 'Login'>;

const DIAL_CODE = '+1';

type Step = 'google' | 'phone';

export function LoginScreen({ navigation }: Props) {
  const [step, setStep] = useState<Step>('google');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isSending, setIsSending] = useState(false);
  const { handleGoogleLogin, isGoogleLoading } = useGoogleLogin();
  const { handleAppleLogin, isAppleLoading, isAppleAvailable } = useAppleLogin();
  const busy = isSending || isGoogleLoading || isAppleLoading;

  const handleContinuePhone = async () => {
    const digits = phoneNumber.replace(/\D/g, '');
    if (!/^\d{7,15}$/.test(digits)) {
      Alert.alert('Invalid number', 'Enter a valid phone number (7–15 digits).');
      return;
    }

    const fullNumber = `${DIAL_CODE}${digits}`;
    setIsSending(true);
    try {
      const deviceId = await getDeviceId();
      await authService.sendOTP(fullNumber, deviceId);
      navigation.navigate('OtpVerify', {
        phoneNumber: fullNumber,
        dialCode: DIAL_CODE,
        localNumber: digits,
      });
    } catch (error) {
      Alert.alert('Error', error instanceof Error ? error.message : 'Failed to send code');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <AuthModalFrame>
      {step === 'google' ? (
        <>
          <AuthBrandHeader
            headline={signInHeadline()}
            subtitle="Sign in with Apple or Google — or use your phone if you prefer."
          />

          <View style={styles.form}>
            {isAppleAvailable ? (
              <AppleSignInButton
                onPress={handleAppleLogin}
                disabled={busy}
                loading={isAppleLoading}
              />
            ) : null}

            <GoogleSignInButton
              onPress={handleGoogleLogin}
              disabled={busy}
              loading={isGoogleLoading}
            />

            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Use phone number instead"
              disabled={busy}
              onPress={() => setStep('phone')}
              style={({ pressed }) => [styles.phoneLink, pressed && styles.pressed]}
            >
              <Text style={styles.phoneLinkText}>Use phone number instead</Text>
            </Pressable>
          </View>

          <GlassButton variant="ghost" fullWidth onPress={goBackFromAuth} disabled={busy}>
            Not now
          </GlassButton>
        </>
      ) : (
        <>
          <AuthBrandHeader
            headline="Phone number"
            subtitle="We’ll text you a one-time code to sign in."
          />

          <View style={styles.form}>
            <Text style={styles.label}>Phone</Text>
            <AuthPhoneField
              dialCode={DIAL_CODE}
              value={phoneNumber}
              onChangeText={setPhoneNumber}
              editable={!busy}
            />
            <GlassButton
              variant="primary"
              fullWidth
              onPress={handleContinuePhone}
              disabled={busy}
            >
              {isSending ? 'Sending…' : 'Send verification code'}
            </GlassButton>
            {isSending ? <ActivityIndicator color={colors.accent} /> : null}
          </View>

          <GlassButton
            variant="ghost"
            fullWidth
            onPress={() => setStep('google')}
            disabled={busy}
          >
            Back to sign-in
          </GlassButton>
        </>
      )}
    </AuthModalFrame>
  );
}

const styles = StyleSheet.create({
  form: {
    gap: spacing.md,
  },
  label: {
    ...typography.label,
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  phoneLink: {
    alignItems: 'center',
    paddingVertical: spacing.sm,
    minHeight: 44,
    justifyContent: 'center',
  },
  phoneLinkText: {
    ...typography.body,
    fontSize: 15,
    color: colors.textMuted,
    textDecorationLine: 'underline',
  },
  pressed: {
    opacity: 0.75,
  },
});
